/**
 * @file change-password-modal.tsx
 * @description
 * -----------------------------------------------------
 * Archivo que contiene el modal para cambiar la contraseña
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2026-04-07
 * @author DLTCode
 * @author Sergio Manjón
 * @copyright © DLTCode
 * @license Private
 * */

"use client";

import React, { useState, useTransition } from "react";
import ModalComponent from "@/components/modals/ModalComponent";
import InputComponent from "@/components/ui/InputComponent";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { ChangePassword, changePassword, useProfileView } from "@/features/profile";
import { PROFILE_CHANGE_PASSWORD_MODAL_ID } from "./profile-modal-ids";
export const ChangePasswordModal = () => {
  const modals = useTranslations("Modals.ChangePassword");
  const labels = useTranslations("Labels");
  const placeholders = useTranslations("Placeholders");
  const responseMessagesT = useTranslations("ResponseMessages");
  const {
    closeChangePasswordModal,
    isChangePasswordModalOpen
  } = useProfileView();
  const [isPending, startTransition] = useTransition();
  const [formData, setFormData] = useState<ChangePassword>({
    oldPassword: "",
    newPassword: "",
    repeatPassword: ""
  });
  const [errors, setErrors] = useState<{
    [key: string]: string[];
  }>({});
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      id,
      value
    } = event.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
    if (errors[id]) {
      setErrors(prevErrors => {
        const newErrors = {
          ...prevErrors
        };
        delete newErrors[id];
        return newErrors;
      });
    }
  };
  const handleSubmit = async (formData_0: FormData) => {
    startTransition(async () => {
      try {
        const response = await changePassword(formData_0);
        if (response && "fieldErrors" in response) {
          setErrors(response.fieldErrors);
          return;
        }
        if (response && "error" in response) {
          toast.error(response.message || responseMessagesT("PASSWORD_USER_ERROR"));
          return;
        }
        toast.success(response?.message || responseMessagesT("PASSWORD_USER"));
        setFormData({
          oldPassword: "",
          newPassword: "",
          repeatPassword: ""
        });
        closeChangePasswordModal();
      } catch (error) {
        if (error instanceof Error) {
          setErrors({
            general: [error.message]
          });
        }
      }
    });
  };
  return <ModalComponent<ChangePassword> id={PROFILE_CHANGE_PASSWORD_MODAL_ID} title={modals("title")} isOpen={isChangePasswordModalOpen} onClose={closeChangePasswordModal} formAction={handleSubmit} loadingConfirm={isPending} formData={{
    oldPassword: formData.oldPassword,
    newPassword: formData.newPassword,
    repeatPassword: formData.repeatPassword ?? ""
  }} buttonSubmitText="buttonSaveChanges" buttonCancel={true} hideSubmitIcon titleAndAria="buttonChangePassword" disabledConfirm={!formData.oldPassword || !formData.newPassword || !formData.repeatPassword}>
      <InputComponent id="oldPassword" name="oldPassword" type="password" label={labels("labelOldPassword")} placeholder={placeholders("placeholderOldPassword")} autoComplete="current-password" className="input--full" value={formData.oldPassword} onChange={handleChange} error={errors.oldPassword && errors.oldPassword[0]} isRequired style={{
      marginBottom: "0.75rem"
    }} />
      <InputComponent id="newPassword" name="newPassword" type="password" label={labels("labelNewPassword")} placeholder={placeholders("placeholderNewPassword")} autoComplete="new-password" className="input--full" value={formData.newPassword} onChange={handleChange} error={errors.newPassword && errors.newPassword[0]} isRequired aria-describedby="new-password-requirements" style={{
      marginBottom: "0.75rem"
    }} />
      <InputComponent id="repeatPassword" name="repeatPassword" type="password" label={labels("labelRepeatNewPassword")} placeholder={placeholders("placeholderRepeatNewPassword")} autoComplete="new-password" className="input--full" value={formData.repeatPassword} onChange={handleChange} error={errors.repeatPassword && errors.repeatPassword[0]} isRequired style={{
      marginBottom: "1.25rem"
    }} />
    </ModalComponent>;
};